import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Send, Loader2, CheckCircle2, ArrowRight, RotateCcw, Save, AlertTriangle, Check, Ruler, MinusCircle, PersonStanding } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn, mapUrl, mapPinUrl, addressPoint } from '@/lib/utils';
import { StreetViewPanel, useStreetView } from '@/components/custom/StreetView';
import { releaseAddress, unreleaseAddress, removeFromScopeList, upsertSiblingMapping, fetchSiblingMapping, updateDeliveryStatus, updateDwellingUnitType } from '@/hooks/useAddressValidation';
import { AddressComboBox } from '@/components/forms/AddressComboBox';
import { PostalCodeLocalityHint } from '@/components/tickets/PostalCodeLocalityHint';
import { useDmpWriteConfirm } from '@/hooks/useDmpWriteConfirm';
import { useAuth } from '@/hooks/useAuth';
import { ApiError } from '@/lib/api-client';
import type { MduApartmentDto, DeclaredUnit } from '@uniticket/shared';
import {
  houseNumberExtensionCoherent, computeAttentionPoints, listSelectableDeliveryCodes,
  isValidDeliveryCode, formatDeliveryStatus, addressStatusForDeliveryCode, duplicatesOfInput,
} from '@uniticket/shared';

// "released" = APPROVED FOR RELEASE — the flag that actually makes the address visible to the partner,
// set by our Release action (approved_for_release + home_released) or by DMP staff on an address that was
// already live before we ever saw it.
//
// It is deliberately NOT read off `status`: that column is the PHYSICAL delivery state, derived from the
// delivery code at creation time (a 9055 pole-drop address is written HOMES_PASSED the moment it is
// created, faithful to the source DMP). Conflating the two made every freshly created address look
// "already released", so the Release form was never offered and the address could not be released at all
// (user bug 2026-08-06, rue du Petit Bâti — the team was told to stop creating addresses).
function isReleased(a: MduApartmentDto): boolean {
  return a.attributes?.approvedForRelease === true || a.attributes?.homeReleased === true;
}

// Colonne DMP nommée par le refus du release → la ligne du verdict qui porte le même libellé (D2708-2).
const NETWORK_COLUMN_CHECK: Record<string, 'areaId' | 'areaPopId' | 'centralPopId'> = {
  area_id: 'areaId', area_pop_id: 'areaPopId', central_pop_id: 'centralPopId',
};

/** 22-10 — la date de build list qui verrouille le delivery status pour un VALIDATEUR (« les admins ont le droit de
 *  le faire, et pas besoin de mettre un motif »). null = pas verrouillé. Le serveur tient la même règle. */
function useDeliveryLock(a: MduApartmentDto): string | null {
  const { isAdmin } = useAuth();
  return !isAdmin && a.attributes?.scopelistV2 ? a.attributes.scopelistV2 : null;
}

// Pre-fill delivery_status from a sibling that is already HOMES_CONNECT/HOMES_PASSED and carries a value.
function siblingDeliveryDefault(target: MduApartmentDto, siblings: MduApartmentDto[]): number | null {
  const s = siblings.find(
    (x) =>
      x.jvid !== target.jvid &&
      (x.attributes?.status === 'HOMES_CONNECT' || x.attributes?.status === 'HOMES_PASSED') &&
      x.attributes?.deliveryStatus != null,
  );
  return s?.attributes?.deliveryStatus ?? null;
}

// Right pane of the per-apartment sheet — a 3-state machine:
//  • released (home_released=true) → "Already released" + the mapping-table edit form (pre-filled from the
//    reference) + the Un-release action (with an inline confirmation).
//  • approved for release without our flag (released from base in the DMP) → the same manage panel.
//  • not approved for release → the Release form (delivery_status + length required, validated client-side).
//    This is where a freshly created address lands, whatever `status` its delivery code produced.
export function ReleaseAddressPanel({
  sibling,
  siblings,
  duplicateCandidates = [],
  ticketId,
  onReleased,
  onClose,
  hideMappingForm = false,
  actionsDisabled = false,
}: {
  sibling: MduApartmentDto;
  siblings: MduApartmentDto[];
  /** N41 ⑤ — les logements de la même adresse postale rangés sous un AUTRE building ID : la recherche de doublons
   *  de déclaration (N38) les regarde aussi. Ce ne sont PAS des `siblings` — ni le défaut de livraison ni le
   *  décompte du type de logement ne doivent les voir. */
  duplicateCandidates?: DeclaredUnit[];
  ticketId: string;
  onReleased: () => void;
  onClose: () => void;
  /** Hide the mapping-table edit form of the released state. Used when the sheet shows the ticket's OWN
   *  address (SDU path): its mapping row is the same (jvid, operator) row Panel 2 already edits — two
   *  live editors of one row would silently overwrite each other. */
  hideMappingForm?: boolean;
  /** Anti-collision lock: another user is working this ticket — the sheet stays readable but the
   *  release / un-release / mapping-save actions are disabled (the server 423s them anyway). */
  actionsDisabled?: boolean;
}) {
  const { t } = useTranslation(['tickets']);

  // Show the mapping-table edit form for ANY address already visible to the customer — whether released via
  // THIS app (home_released flag, set by our Release action) OR already released "from base" in the DMP
  // (status HOMES_PASSED/HOMES_CONNECT, no home_released flag). Editing only touches address_mapping_table,
  // so it is safe regardless of how the address became visible. Un-release is offered ONLY for an
  // app-released address (home_released=true): un-releasing flips public.address back to NOT_AVAILABLE/amber,
  // which must never run against an address we never released.
  const released = sibling.attributes?.homeReleased === true;
  const alreadyVisible = isReleased(sibling);

  // Offered in BOTH states: leaving a scope list and being published are two different decisions — an
  // address sits in the deployment list whether or not it was ever released, so the block belongs to the
  // sheet, not to one of its branches.
  const scopeLists = (
    <ScopeListRemoval sibling={sibling} onChanged={onReleased} actionsDisabled={actionsDisabled} />
  );

  // Same reasoning as scopeLists, and the same placement: correcting the delivery code is neither a
  // release nor a consequence of one, so it belongs to the SHEET rather than to one of its branches.
  const deliveryEdit = (
    <DeliveryStatusEdit sibling={sibling} onChanged={onReleased} actionsDisabled={actionsDisabled} />
  );

  // Same placement rule again: the dwelling type is neither a release nor a consequence of one.
  const dwellingTypeEdit = (
    <DwellingTypeEdit
      sibling={sibling}
      siblings={siblings}
      onChanged={onReleased}
      actionsDisabled={actionsDisabled}
    />
  );

  // Aller voir l'endroit : c'est le geste qu'on fait sur TOUTE adresse, released ou non. Le bouton
  // vivait dans le seul formulaire de release, donc invisible sur une adresse déjà publiée — alors
  // que c'est souvent là qu'on veut vérifier ce qui a été livré. Même règle de placement que les
  // trois blocs ci-dessus : il appartient à la fiche, pas à une de ses branches.
  const mapLinks = <AddressMapLinks sibling={sibling} />;

  if (released || alreadyVisible) {
    return (
      <>
        <ReleasedManagePanel
          sibling={sibling}
          siblings={siblings}
          duplicateCandidates={duplicateCandidates}
          ticketId={ticketId}
          onChanged={onReleased}
          onClose={onClose}
          appReleased={released}
          hideMappingForm={hideMappingForm}
          actionsDisabled={actionsDisabled}
        />
        {mapLinks}
        {deliveryEdit}
        {dwellingTypeEdit}
        {scopeLists}
      </>
    );
  }

  return (
    <div>
      <h3 className="text-sm font-bold text-slate-800 mb-1">{t('tickets:validation.release.title')}</h3>
      <p className="text-[11px] text-slate-400 mb-3">{t('tickets:validation.release.subtitle')}</p>
      {/* DMP release block (home_release_blocked_reason): releasing anyway stays POSSIBLE (the block may
          be stale) but must be a conscious act — the reviewer should check with Build first. */}
      {sibling.attributes?.homeReleaseBlockedReason && (
        <div className="flex items-start gap-2 mb-3 rounded-md border border-red-300 bg-red-50 px-3 py-2" role="alert">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-red-600" />
          <span className="text-xs text-red-800">
            {t('tickets:validation.release.blockedWarning', {
              reason: sibling.attributes.homeReleaseBlockedReason,
              date: sibling.attributes.homeReleaseBlockedAt ?? '—',
            })}
          </span>
        </div>
      )}
      <ReleaseForm sibling={sibling} siblings={siblings} onReleased={onReleased} actionsDisabled={actionsDisabled} />
      {mapLinks}
      {deliveryEdit}
      {dwellingTypeEdit}
      {scopeLists}
    </div>
  );
}

// -- Aller voir l'adresse sur Google (demande user, réunion 2026-08-27) --------------------------
// LE POINT VIENT DES COORDONNÉES DU DMP, PAS DU TEXTE DE L'ADRESSE. Le lien d'avant passait la rue,
// le numéro, le code postal et la ville à une RECHERCHE Maps : Google re-géocodait pour son compte et
// tranchait lui-même sur l'adresse — celle-là même qui est en litige dans le ticket qu'on valide, et
// qu'il ne connaît souvent pas quand elle vient d'être créée. `reference.lat/lon` est la projection du
// x/y Belmap stocké, il voyageait déjà dans la réponse sans que personne ne s'en serve.
//
// Deux gestes distincts, donc deux boutons :
//  • Street View — depuis le 2026-10-16, le panneau du viewer partagé d'Unifiber, DANS l'app
//    (components/custom/StreetView.tsx), au lieu d'un onglet Google : la fiche reste sous les yeux. Le
//    même que le bonhomme de la carte « Référence adresse » et de la console. Sans coordonnées il se
//    grise : le viewer ne sait pas partir d'un texte.
//  • Mesurer — épingle sur le point exact, pour tracer rue → bâtiment. Réservé au SDU, qui est le seul
//    à avoir une distance à remplir (règle Anouk 2026-08-04).
//
// LE POINT part des x/y (`addressPoint`, 2026-10-16), plus du lat/lon stocké : celui-là est faux de
// ~836 m sur toutes les adresses que l'app a créées ([[address-validation-lambert72-bug]]).
//
// HIÉRARCHIE DEMANDÉE PAR LE USER (2026-08-28) : les coordonnées d'abord, l'adresse en secours.
// Elle se joue à DEUX échelons, parce qu'un seul ne suffit pas.
//   1. AUTOMATIQUE — pas de coordonnées (`reference` est null sur le repli snapshot, ou x/y et lat/lon
//      absents) → Street View se grise, la carte de mesure part du texte, et la légende le dit.
//   2. MANUEL — les coordonnées sont là mais le point tombe à côté. Ça, le code ne peut PAS le voir :
//      une fois l'URL remise au navigateur, on ne sait plus ce que Google en a fait. Cas réels : aucun
//      panorama à cet endroit (voirie privée, campagne), ou un x/y lui-même faux. D'où le lien de
//      repli, toujours offert sous les boutons : c'est le relecteur qui voit que ça n'a pas marché,
//      donc c'est à lui qu'il faut donner le second échelon.
function AddressMapLinks({ sibling }: { sibling: MduApartmentDto }) {
  const { t } = useTranslation(['tickets']);
  const isMdu = String(sibling.reference?.dwellingUnitType ?? '').trim().toUpperCase() === 'MDU';
  const point = addressPoint(sibling.reference);
  const sv = useStreetView(point);
  const number = (sibling.houseNumberExtension || sibling.houseNumber || '').trim() || null;
  const textFallback = mapUrl([
    sibling.streetName,
    number,
    sibling.reference?.postalCode,
    sibling.reference?.city,
  ]);
  const svLabel = [
    [sibling.streetName, number].filter(Boolean).join(' ') + (sibling.busNumber?.trim() ? ` / ${sibling.busNumber}` : ''),
    [sibling.reference?.postalCode, sibling.reference?.city].filter(Boolean).join(' '),
  ].filter(Boolean).join(' · ');

  return (
    <div className="mt-4">
      <Button
        type="button"
        variant="outline"
        className="w-full mb-1"
        onClick={sv.toggle}
        disabled={!point}
        title={point ? undefined : t('tickets:streetViewPanel.noCoords')}
        aria-pressed={point ? sv.open : undefined}
      >
        <PersonStanding className={cn('mr-1.5 h-4 w-4', point && 'text-amber-500')} />
        {t('tickets:validation.release.streetView')}
      </Button>
      {sv.open && point && <StreetViewPanel point={point} label={svLabel} onClose={sv.close} />}
      {!isMdu && (
        <Button asChild variant="outline" className="w-full mb-1">
          <a
            href={point ? mapPinUrl(point.lat, point.lon) : textFallback}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Ruler className="mr-1.5 h-4 w-4" />
            {t('tickets:validation.release.measureMap')}
          </a>
        </Button>
      )}
      <p className="text-[10px] text-slate-400">
        {point
          ? t('tickets:validation.release.mapFromCoords')
          : t('tickets:validation.release.mapNoCoords') + (isMdu ? '' : ` ${t('tickets:validation.release.mapFromText')}`)}
        {!isMdu && ` ${t('tickets:validation.release.measureMapHint')}`}
      </p>
      {point && (
        <p className="mt-1 text-[10px] text-slate-400">
          {t('tickets:validation.release.mapFallbackPrompt')}{' '}
          <a
            href={textFallback}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-brand-rose hover:underline"
          >
            {t('tickets:validation.release.mapFallbackLink')}
          </a>
        </p>
      )}
    </div>
  );
}



// -- Dwelling type -- SDU <-> MDU on THIS row, on demand (user ask 2026-08-28) --
// `dwelling_unit_type` lives in two tables. The validation window already corrects the operator's
// declaration in address_mapping_table -- that is the amber "MDU ≠ DMP ref." field of Panel 2 -- but the
// DMP's OWN column had no write path at all outside address creation. It is the one an operator means
// when they ask « pouvez-vous modifier l'adresse en MDU? » (UT-2026-000194), so answering only in the
// mapping answered somewhere they do not read.
//
// ONE ROW, and never automatically. The audit of 2026-08-28 found 923 buildings whose type looks wrong,
// and it would have been easy to offer a per-building sweep -- but a building of three units where two
// are OUT OF SCOPE is a SDU, so no count can decide this. The block therefore states the in-scope tally
// and stops there: it informs the judgement, it never makes it.
function DwellingTypeEdit({
  sibling,
  siblings,
  onChanged,
  actionsDisabled = false,
}: {
  sibling: MduApartmentDto;
  siblings: MduApartmentDto[];
  onChanged: () => void;
  actionsDisabled?: boolean;
}) {
  const { t } = useTranslation(['tickets']);
  const confirmDmpWrite = useDmpWriteConfirm();
  const current = (sibling.reference?.dwellingUnitType ?? '').trim().toUpperCase();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const inScope = siblings.filter((a) => a.attributes?.scopelistV15).length;

  async function apply(next: 'SDU' | 'MDU') {
    if (next === current) return;
    if (!confirmDmpWrite(t('tickets:validation.dwellingType.confirmAction', { type: next }))) return;
    setBusy(next);
    setError(null);
    try {
      const res = await updateDwellingUnitType(sibling.jvid, next);
      if (res.data.updated === 0) toast.warning(t('tickets:validation.dwellingType.noop'));
      else toast.success(t('tickets:validation.dwellingType.done', { type: next }));
      onChanged();
    } catch (err: any) {
      setError(err?.message || t('tickets:validation.dwellingType.error'));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="border-t border-slate-100 pt-3 mt-4">
      <h3 className="text-sm font-bold text-slate-800 mb-1">{t('tickets:validation.dwellingType.title')}</h3>
      <p className="text-[11px] text-slate-400 mb-2">{t('tickets:validation.dwellingType.subtitle')}</p>

      {siblings.length > 1 && (
        <p className="text-[11px] text-slate-500 mb-2">
          {t('tickets:validation.dwellingType.scopeTally', { total: siblings.length, inScope })}
        </p>
      )}

      <div className="flex gap-2">
        {(['SDU', 'MDU'] as const).map((type) => {
          const isCurrent = type === current;
          return (
            <Button
              key={type}
              variant="outline"
              onClick={() => apply(type)}
              disabled={isCurrent || busy !== null || actionsDisabled}
              className={cn(
                'flex-1 justify-center',
                isCurrent && 'border-emerald-300 bg-emerald-50 text-emerald-800 font-semibold disabled:opacity-100',
              )}
            >
              {busy === type ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
              {type}
              {isCurrent && <Check className="w-3.5 h-3.5 ml-1.5" />}
            </Button>
          );
        })}
      </div>

      {error && <p className="mt-1.5 text-[11px] font-medium text-rose-600" role="alert">{error}</p>}
      <p className="mt-1.5 text-[11px] text-slate-400">{t('tickets:validation.dwellingType.hint')}</p>
    </div>
  );
}

// -- Delivery status -- correct the code WITHOUT releasing (user ask 2026-08-28) --
// Offered in BOTH states of the sheet, for the same reason the scope-list block above is: correcting a
// code and publishing an address are two different decisions. Until now the only field where a delivery
// status could be typed was the Release form, so fixing 7013 into 7015 meant RELEASING the address --
// and on an address already released it was flatly impossible, since it would have taken an un-release
// first, which HOMES_CONNECT refuses.
//
// The picker is built from the shared catalogue: the new 1101-1120 family first (the norm from
// 1 September 2026), then the composite the existing ~396 000 addresses carry. Blocked codes are not
// offered -- declaring an address non-serviceable is a business decision, not a transcription fix -- but
// they remain typable, so nothing is made impossible.
const DELIVERY_OPTIONS = listSelectableDeliveryCodes().map((c) => ({ value: String(c.code), label: c.label }));

function DeliveryStatusEdit({
  sibling,
  onChanged,
  actionsDisabled = false,
}: {
  sibling: MduApartmentDto;
  onChanged: () => void;
  actionsDisabled?: boolean;
}) {
  const { t } = useTranslation(['tickets']);
  const confirmDmpWrite = useDmpWriteConfirm();
  const lockedSince = useDeliveryLock(sibling);
  const currentCode = sibling.attributes?.deliveryStatus ?? null;
  const [picked, setPicked] = useState<string>(currentCode != null ? String(currentCode) : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const next = Number(picked);
  const valid = picked.trim() !== '' && Number.isInteger(next) && isValidDeliveryCode(next);
  const moved = valid && next !== currentCode;

  // The one contradiction worth naming: declaring the unit "not deployed" while a customer is live on it
  // or while it is published. Warned, never refused -- sometimes that IS the correction being made.
  const contradicts = moved
    && addressStatusForDeliveryCode(next) === 'NOT_AVAILABLE'
    && (sibling.attributes?.status === 'HOMES_CONNECT' || isReleased(sibling));

  async function save() {
    if (!moved) return;
    if (!confirmDmpWrite(t('tickets:validation.deliveryEdit.confirmAction', { code: next }))) return;
    setBusy(true);
    setError(null);
    try {
      const res = await updateDeliveryStatus(sibling.jvid, next);
      if (res.data.updated === 0) toast.warning(t('tickets:validation.deliveryEdit.noop'));
      else toast.success(t('tickets:validation.deliveryEdit.done', { code: next }));
      onChanged();
    } catch (err: any) {
      setError(err?.message || t('tickets:validation.deliveryEdit.error'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="border-t border-slate-100 pt-3 mt-4">
      <h3 className="text-sm font-bold text-slate-800 mb-1">{t('tickets:validation.deliveryEdit.title')}</h3>
      <p className="text-[11px] text-slate-400 mb-2">{t('tickets:validation.deliveryEdit.subtitle')}</p>
      <p className="text-[11px] text-slate-500 mb-2">
        {t('tickets:validation.deliveryEdit.current')} <span className="font-medium text-slate-700">{formatDeliveryStatus(currentCode)}</span>
      </p>

      {/* 22-10 — en build list, un validateur voit le code mais ne le change pas : le bloc le dit au lieu d'offrir
          un champ que le serveur refuserait. */}
      {lockedSince ? (
        <p className="flex items-start gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600" role="note">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-500" />
          {t('tickets:validation.deliveryEdit.lockedBuildList', { date: lockedSince })}
        </p>
      ) : (
      <>
      <AddressComboBox
        value={picked}
        label={valid ? formatDeliveryStatus(next) : picked}
        onChange={(opt) => { setPicked(opt?.value ?? ''); setError(null); }}
        fetcher={async (q) => {
          const needle = q.trim().toLowerCase();
          if (!needle) return DELIVERY_OPTIONS.slice(0, 40);
          return DELIVERY_OPTIONS.filter(
            (o) => o.value.startsWith(needle) || o.label.toLowerCase().includes(needle),
          ).slice(0, 40);
        }}
        allowCustom
        customLabel={t('tickets:validation.deliveryEdit.useCode')}
        disabled={busy || actionsDisabled}
        error={picked.trim() !== '' && !valid}
        placeholder={t('tickets:validation.deliveryEdit.placeholder')}
        emptyText={t('tickets:validation.deliveryEdit.noMatch')}
        searchingText={t('tickets:validation.deliveryEdit.searching')}
        clearLabel={t('tickets:validation.deliveryEdit.clear')}
      />

      {picked.trim() !== '' && !valid && (
        <p className="mt-1.5 text-[11px] font-medium text-rose-600" role="alert">
          {t('tickets:validation.deliveryEdit.invalid', { code: picked })}
        </p>
      )}

      {contradicts && (
        <div className="flex items-start gap-2 mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2" role="note">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
          <span className="text-xs text-amber-800">{t('tickets:validation.deliveryEdit.contradicts')}</span>
        </div>
      )}

      {error && <p className="mt-1.5 text-[11px] font-medium text-rose-600" role="alert">{error}</p>}

      <Button
        onClick={save}
        disabled={!moved || busy || actionsDisabled}
        variant="outline"
        className="w-full mt-2 justify-center"
      >
        {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
        {moved ? t('tickets:validation.deliveryEdit.save') : t('tickets:validation.deliveryEdit.noChange')}
      </Button>
      <p className="mt-1.5 text-[11px] text-slate-400">{t('tickets:validation.deliveryEdit.hint')}</p>
      </>
      )}
    </div>
  );
}

type ScopeTier = 'v15';

// ── Scope lists — clear the DEPLOYMENT (v15) date from the sheet (user ask 2026-08-13) ──
// The build list (v2) was removed on 2026-08-28 (user decision): an address must never leave it, so the
// button is gone and ScopeTier narrowed to one value — the server refuses v2 by name anyway.
// A tier with no date shows no button: there is nothing to clear, and the presence of the button IS the
// state. When it is not set the whole block disappears, so the sheet stays quiet on the addresses this
// does not concern. The date is displayed next to its DMP field name because the app labels these columns
// "Deployment list" / "Build list" everywhere else — the two vocabularies have to meet somewhere.
function ScopeListRemoval({
  sibling,
  onChanged,
  actionsDisabled = false,
}: {
  sibling: MduApartmentDto;
  onChanged: () => void;
  actionsDisabled?: boolean;
}) {
  const { t } = useTranslation(['tickets']);
  const confirmDmpWrite = useDmpWriteConfirm();
  const [confirming, setConfirming] = useState<ScopeTier | null>(null);
  const [busy, setBusy] = useState<ScopeTier | null>(null);

  const dates: Record<ScopeTier, string | null> = {
    v15: sibling.attributes?.scopelistV15 ?? null,
  };
  // HOMES_CONNECT = an active customer connection. The server refuses (ScopeListRemovalForbiddenError);
  // saying so here beats offering a button that can only 409.
  const isConnected = sibling.attributes?.status === 'HOMES_CONNECT';

  if (!dates.v15) return null;

  async function handleRemove(tier: ScopeTier) {
    if (!confirmDmpWrite('retirer cette adresse de la Scope List V15 (liste de déploiement)')) return;
    setBusy(tier);
    try {
      const res = await removeFromScopeList(sibling.jvid, tier);
      // 0 row touched = the date had already gone between the sheet being drawn and the click. Saying
      // "removed" there would be a false report, so the message says what actually happened.
      if (res.data.updated === 0) toast.warning(t('tickets:validation.scopeList.noop'));
      else toast.success(t(`tickets:validation.scopeList.done.${tier}`));
      setBusy(null);
      setConfirming(null);
      onChanged();
    } catch (err: any) {
      toast.error(err?.message || t('tickets:validation.scopeList.error'));
      setBusy(null);
      setConfirming(null);
    }
  }

  function tierRow(tier: ScopeTier, date: string) {
    if (confirming === tier) {
      return (
        <div key={tier} className="rounded-md border border-amber-300 bg-amber-50 p-3">
          <p className="flex items-start gap-1.5 text-xs text-amber-800 mb-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            {t(`tickets:validation.scopeList.confirm.${tier}`, { date })}
          </p>
          <div className="flex gap-2">
            <Button
              onClick={() => handleRemove(tier)}
              disabled={busy !== null || actionsDisabled}
              className="flex-1 bg-amber-600 hover:bg-amber-700"
            >
              {busy === tier ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
              {busy === tier ? t('tickets:validation.scopeList.removing') : t('tickets:validation.scopeList.confirmYes')}
            </Button>
            <Button variant="outline" onClick={() => setConfirming(null)} disabled={busy !== null} className="flex-1">
              {t('tickets:validation.release.cancel')}
            </Button>
          </div>
        </div>
      );
    }
    return (
      <div key={tier}>
        <Button
          variant="outline"
          onClick={() => setConfirming(tier)}
          disabled={busy !== null || actionsDisabled}
          className="w-full justify-start text-amber-700 border-amber-300 hover:bg-amber-50"
        >
          <MinusCircle className="w-4 h-4 mr-1.5" />{t(`tickets:validation.scopeList.remove.${tier}`)}
        </Button>
        <p className="mt-1 text-[11px] text-slate-400">
          {t(`tickets:validation.scopeList.field.${tier}`)} · {date}
        </p>
      </div>
    );
  }

  return (
    <div className="border-t border-slate-100 pt-3 mt-4">
      <h3 className="text-sm font-bold text-slate-800 mb-1">{t('tickets:validation.scopeList.title')}</h3>
      <p className="text-[11px] text-slate-400 mb-3">{t('tickets:validation.scopeList.subtitle')}</p>
      {isConnected ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
          <p className="flex items-start gap-1.5 text-xs text-amber-800" role="note">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{t('tickets:validation.scopeList.blockedConnect')}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {dates.v15 && tierRow('v15', dates.v15)}
        </div>
      )}
    </div>
  );
}

// ── Release form — delivery_status + length_estimate_from_street are BOTH required and validated BEFORE
// the request fires; a missing/invalid field shows an inline error by the button (no request, no toast). ──
function ReleaseForm({
  sibling,
  siblings,
  onReleased,
  actionsDisabled = false,
}: {
  sibling: MduApartmentDto;
  siblings: MduApartmentDto[];
  onReleased: () => void;
  actionsDisabled?: boolean;
}) {
  const { t } = useTranslation(['tickets']);
  // MDU ⇒ no street distance at all (user rule 2026-08-04). Anouk: « la distance depuis la rue est
  // faite par HP, on ne va pas l'estimer pour un HC — c'est principalement pour les SDU ». The field
  // is HIDDEN rather than merely optional (explicit user decision) so no `0` of convenience can be
  // typed; the server forces both the distance AND distance_source to NULL either way.
  // 22-10 — un faux SDU (règle d'Anouk) se release comme un MDU : pas de distance, et le serveur le passe en MDU.
  const fakeSdu = sibling.attributes?.fakeSdu === true;
  const isMdu = String(sibling.reference?.dwellingUnitType ?? '').trim().toUpperCase() === 'MDU' || fakeSdu;
  // 22-10 — en build list, un validateur release avec le code ENREGISTRÉ, sans pouvoir le changer (même vide :
  // le serveur ne va pas non plus le chercher chez un frère). Un admin garde le champ.
  const lockedSince = useDeliveryLock(sibling);
  const ownCode = sibling.attributes?.deliveryStatus ?? null;
  const deliveryDefault = useMemo(
    () => (lockedSince ? ownCode : siblingDeliveryDefault(sibling, siblings)),
    [lockedSince, ownCode, sibling, siblings],
  );
  const [delivery, setDelivery] = useState(deliveryDefault != null ? String(deliveryDefault) : '');
  const [length, setLength] = useState('');
  const [releasing, setReleasing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirmDmpWrite = useDmpWriteConfirm();

  const today = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

  async function handleRelease() {
    const missing: string[] = [];
    if (!lockedSince && (delivery.trim() === '' || Number.isNaN(Number(delivery)))) missing.push(t('tickets:validation.release.deliveryStatus'));
    // The distance is required for an SDU only — on an MDU the field isn't even rendered.
    if (!isMdu && (length.trim() === '' || Number.isNaN(Number(length)))) missing.push(t('tickets:validation.release.lengthEstimate'));
    if (missing.length > 0) {
      setError(t('tickets:validation.release.errRequired', { fields: missing.join(', ') }));
      return;
    }
    setError(null);
    if (!confirmDmpWrite('publier cette adresse (Release)')) return;
    setReleasing(true);
    try {
      const res = await releaseAddress(sibling.jvid, {
        // Verrouillé : rien n'est envoyé, le serveur garde le code de la ligne.
        ...(lockedSince ? {} : { deliveryStatus: Number(delivery) }),
        // Omitted on an MDU: the distance and its source are only ever written together, and the
        // server forces both to NULL there whatever we send.
        ...(isMdu ? {} : { lengthEstimateFromStreet: Number(length) }),
      });
      toast.success(t('tickets:validation.release.done'));
      if (res.data.quoteRequired) {
        toast.message(t('tickets:validation.release.quoteRequiredSet'));
      }
      if (res.data.fakeOdf?.created) {
        toast.success(t('tickets:validation.release.fakeOdfAdded', { utac: res.data.fakeOdf.utac ?? '' }));
      }
      onReleased();
    } catch (err: any) {
      setError(networkIdsRefusal(err) ?? (err?.message || t('tickets:validation.release.error')));
    } finally {
      setReleasing(false);
    }
  }

  // D2708-2 (2026-10-15) — le refus « identifiants réseau absents » arrive avec un code et les colonnes en cause :
  // on le dit en français, avec les libellés des lignes rouges de la fiche, et on dit QUOI FAIRE. Sur un MDU, le
  // bouton « Déduire du bâtiment » est sur ces lignes ; sur un SDU, il n'y en a pas (règle d'Anouk : souvent hors
  // scope), donc on escalade. null = un autre refus, rendu tel que le serveur l'a écrit.
  function networkIdsRefusal(err: unknown): string | null {
    if (!(err instanceof ApiError) || err.body?.code !== 'RELEASE_FIELDS_REQUIRED') return null;
    const missing = Array.isArray(err.body.missing) ? (err.body.missing as unknown[]).map(String) : [];
    const labels = missing.map((c) => (NETWORK_COLUMN_CHECK[c] ? t(`tickets:validation.serviceFile.checks.${NETWORK_COLUMN_CHECK[c]}`) : c));
    return `${t('tickets:validation.release.errNetworkIds', { fields: labels.join(', ') || '—' })} `
      + t(isMdu ? 'tickets:validation.release.errNetworkIdsMdu' : 'tickets:validation.release.errNetworkIdsSdu');
  }

  return (
    <>
      <div className="rounded-lg border border-slate-100 bg-slate-50/60 p-3 mb-4">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
          {t('tickets:validation.release.willSet')}
        </p>
        <ul className="space-y-1 text-xs text-slate-600">
          <li className="flex items-center gap-1.5"><ArrowRight className="w-3 h-3 text-brand-rose shrink-0" />{t('tickets:validation.release.willHomeReleased')}</li>
          <li className="flex items-center gap-1.5"><ArrowRight className="w-3 h-3 text-brand-rose shrink-0" />{t('tickets:validation.release.willStatus')}</li>
          <li className="flex items-center gap-1.5"><ArrowRight className="w-3 h-3 text-brand-rose shrink-0" />{t('tickets:validation.release.willDeployment', { date: today })}</li>
          {fakeSdu && (
            <li className="flex items-center gap-1.5"><ArrowRight className="w-3 h-3 text-brand-rose shrink-0" />{t('tickets:validation.release.willFlipMdu')}</li>
          )}
        </ul>
      </div>

      <label className="block text-xs font-medium text-slate-600 mb-1">
        {t('tickets:validation.release.deliveryStatus')} {!lockedSince && <span className="text-red-500">*</span>}
      </label>
      <Input
        type="number"
        value={delivery}
        onChange={(e) => { setDelivery(e.target.value); setError(null); }}
        placeholder={t('tickets:validation.release.deliveryPlaceholder')}
        disabled={!!lockedSince}
        title={lockedSince ? t('tickets:validation.deliveryEdit.lockedBuildList', { date: lockedSince }) : undefined}
      />
      <p className="text-[10px] text-slate-400 mt-1 mb-3">
        {lockedSince
          ? t('tickets:validation.deliveryEdit.lockedBuildList', { date: lockedSince })
          : t('tickets:validation.release.deliveryHint')}
      </p>

      {/* Street distance — SDU ONLY. On an MDU the field is not rendered at all and a one-liner says
          why, so nobody looks for it (user rule 2026-08-04). */}
      {isMdu ? (
        <p className="mb-3 text-[11px] text-slate-400">
          {t(fakeSdu ? 'tickets:validation.release.lengthFakeSduNote' : 'tickets:validation.release.lengthMduNote')}
        </p>
      ) : (
        <>
          <label className="block text-xs font-medium text-slate-600 mb-1">
            {t('tickets:validation.release.lengthEstimate')} <span className="text-red-500">*</span>
          </label>
          <Input
            type="number"
            value={length}
            onChange={(e) => { setLength(e.target.value); setError(null); }}
            placeholder={t('tickets:validation.release.lengthPlaceholder')}
          />
          <p className="text-[10px] text-slate-400 mt-1 mb-3">{t('tickets:validation.release.lengthHint')}</p>
        </>
      )}

      {/* Action-time banners from the shared "Points d'attention" rules, evaluated on the LIVE form
          inputs (quote stamp, aerial anchor, …) — tell the reviewer BEFORE the release. NaN inputs
          simply fire no rule. An MDU carries no distance, so the distance-based rules never fire. */}
      {computeAttentionPoints({
        deliveryStatus: delivery.trim() === '' ? null : Number(delivery),
        lengthEstimateFromStreet: isMdu || length.trim() === '' ? null : Number(length),
      }).map((ruleId) => (
        <p key={ruleId} className="mb-3 flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11px] font-medium text-amber-700">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          {t(`tickets:validation.attention.rules.${ruleId}.releaseHint`)}
        </p>
      ))}

      {/* Les liens Google (Street View + mesure) ont quitté ce formulaire le 2026-08-28 : ils valent
          pour TOUTE adresse, released ou non, et vivent maintenant dans AddressMapLinks, rendu par la
          fiche elle-même dans ses deux branches. */}

      {error && (
        <p className="text-xs font-medium text-red-600 mb-2" role="alert">{error}</p>
      )}
      <Button onClick={handleRelease} disabled={releasing || actionsDisabled} className="w-full">
        {releasing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
        {releasing ? t('tickets:validation.release.releasing') : t('tickets:validation.release.button')}
      </Button>
    </>
  );
}

// ── Released / already-visible state — a badge + the mapping-table edit form (textboxes pre-filled from the
// existing mapping, else the reference address, saved to address_mapping_table via upsert) + the Un-release
// action (inline confirm). Un-release is available for BOTH an app-released address AND one already visible
// "from base" in the DMP (per the user: un-release must always be possible — it flips public.address back to
// NOT_AVAILABLE/amber, LOCAL copy only, prod-guarded server-side). `appReleased` only picks the badge wording. ──
function ReleasedManagePanel({
  sibling,
  siblings,
  duplicateCandidates,
  ticketId,
  onChanged,
  onClose,
  appReleased,
  hideMappingForm = false,
  actionsDisabled = false,
}: {
  sibling: MduApartmentDto;
  /** Les logements de la fenêtre : c'est parmi eux que se cherche un doublon de déclaration (N38). */
  siblings: MduApartmentDto[];
  /** … et parmi ceux de la même adresse postale rangés sous un autre building ID (N41 ⑤). */
  duplicateCandidates: DeclaredUnit[];
  ticketId: string;
  onChanged: () => void;
  onClose: () => void;
  appReleased: boolean;
  hideMappingForm?: boolean;
  actionsDisabled?: boolean;
}) {
  const { t } = useTranslation(['tickets']);
  const ref = sibling.reference;
  // A HOMES_CONNECT address has an active customer connection → un-release must be blocked (it would
  // revert a live line to NOT_AVAILABLE). The mapping-edit form below stays available (it is safe).
  const isConnected = sibling.attributes?.status === 'HOMES_CONNECT';
  const [loading, setLoading] = useState(true);
  const [street, setStreet] = useState('');
  const [houseNumber, setHouseNumber] = useState('');
  const [houseExt, setHouseExt] = useState('');
  const [bus, setBus] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [municipality, setMunicipality] = useState('');
  const [city, setCity] = useState('');
  // Snapshot of the pre-filled values → the mapping Save button stays disabled until the reviewer
  // actually edits a field (dirty-check). Null until the form has loaded.
  const [baseline, setBaseline] = useState<Record<string, string> | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [unreleasing, setUnreleasing] = useState(false);
  // DMP-write beta: confirm before a mapping write reaches the real DMP (no-op when the flag is off).
  const confirmDmpWrite = useDmpWriteConfirm();

  // Pre-fill from the EXISTING mapping row when one exists (so a saved edit — e.g. bus 000A — reappears on
  // re-open in the volet), else from the DMP reference (first time, per the original spec). public.address
  // is never modified; only address_mapping_table is read/written here. Keyed per jvid → runs once on open.
  useEffect(() => {
    if (hideMappingForm) { setLoading(false); return; } // form hidden → nothing to pre-fill
    let alive = true;
    // Apply the pre-fill to every field AND record it as the baseline, so the Save button is disabled
    // until the reviewer changes something (the dirty-check derived from `baseline` below).
    const apply = (v: Record<string, string>) => {
      if (!alive) return;
      setStreet(v.street);
      setHouseNumber(v.houseNumber);
      setHouseExt(v.houseExt);
      setBus(v.bus);
      setPostalCode(v.postalCode);
      setMunicipality(v.municipality);
      setCity(v.city);
      setBaseline(v);
    };
    fetchSiblingMapping(ticketId, sibling.jvid)
      .then((res) => {
        const m = res.data;
        apply({
          street: m?.streetName ?? ref?.streetName ?? '',
          houseNumber: m?.houseNumber ?? ref?.houseNumber ?? '',
          houseExt: m?.houseNumberExtension ?? ref?.houseNumberExtension ?? '',
          bus: m?.busNumber ?? ref?.busNumber ?? '',
          postalCode: m?.postalCode ?? ref?.postalCode ?? '',
          municipality: m?.municipality ?? ref?.municipality ?? '',
          city: m?.city ?? ref?.city ?? '',
        });
      })
      .catch(() =>
        apply({
          street: ref?.streetName ?? '',
          houseNumber: ref?.houseNumber ?? '',
          houseExt: ref?.houseNumberExtension ?? '',
          bus: ref?.busNumber ?? '',
          postalCode: ref?.postalCode ?? '',
          municipality: ref?.municipality ?? '',
          city: ref?.city ?? '',
        }),
      )
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId, sibling.jvid]);

  // Dirty-check: the current field values vs the loaded baseline. The mapping Save button is enabled only
  // once at least one field differs (and re-disabled after a successful save, which re-snapshots the baseline).
  const current: Record<string, string> = { street, houseNumber, houseExt, bus, postalCode, municipality, city };
  const isDirty = baseline != null && Object.keys(current).some((k) => current[k] !== baseline[k]);
  // House number vs extension must agree ("7"+"7a" ok, "7"+"8a" not) → block the mapping Save + flag fields.
  const houseExtIncoherent = !houseNumberExtensionCoherent(houseNumber, houseExt);
  // N38 (décision du 08-10) — la saisie donne à ce logement l'adresse qu'un AUTRE logement de la fenêtre porte
  // déjà pour l'opérateur (sa déclaration, sinon le DMP). Dit pendant la frappe ; l'enregistrement reste possible.
  const twins = duplicatesOfInput([...siblings, ...duplicateCandidates], sibling.jvid, {
    streetName: street, houseNumber, houseNumberExtension: houseExt, busNumber: bus,
  });

  async function handleSaveMapping() {
    if (houseExtIncoherent) { toast.error(t('tickets:validation.houseExtIncoherent')); return; }
    if (!confirmDmpWrite('enregistrer le mapping dans le DMP')) return;
    setSaving(true);
    try {
      // Field-level diff vs the baseline shown to the reviewer (existing mapping ?? DMP reference): only the
      // fields actually changed, recorded old→new in the audit log (mirrors the Panel-2 save).
      const AUDIT_NAME: Record<string, string> = {
        street: 'streetName', houseNumber: 'houseNumber', houseExt: 'houseNumberExtension',
        bus: 'busNumber', postalCode: 'postalCode', municipality: 'municipality', city: 'city',
      };
      const editDiff: Record<string, { from: string | null; to: string | null }> = {};
      if (baseline) {
        for (const k of Object.keys(current)) {
          if (current[k] !== baseline[k]) editDiff[AUDIT_NAME[k]] = { from: baseline[k] || null, to: current[k] || null };
        }
      }
      await upsertSiblingMapping(ticketId, sibling.jvid, {
        addrStreet: street.trim() || null,
        addrHouseNumber: houseNumber.trim() || null,
        houseNumberExtension: houseExt.trim() || null,
        addrBusNumber: bus.trim() || null,
        addrPostalCode: postalCode.trim() || null,
        addrMunicipality: municipality.trim() || null,
        addrCity: city.trim() || null,
        addrDwellingUnitType: ref?.dwellingUnitType ?? null,
        ...(Object.keys(editDiff).length ? { edits: editDiff } : {}),
      });
      toast.success(t('tickets:validation.release.mappingDone'));
      // Saved values become the new baseline → the button disables again until the next edit.
      setBaseline({ ...current });
      // Refresh the page panels so the "Other apartments" table shows the new mapped bus immediately
      // (the #2a overlay), without a manual "Refresh from DMP".
      onChanged();
    } catch (err: any) {
      toast.error(err?.message || t('tickets:validation.release.mappingError'));
    } finally {
      setSaving(false);
    }
  }

  async function handleUnrelease() {
    if (!confirmDmpWrite('annuler la publication (Un-release)')) return;
    setUnreleasing(true);
    try {
      await unreleaseAddress(sibling.jvid);
      toast.success(t('tickets:validation.release.unreleaseDone'));
      onChanged();
    } catch (err: any) {
      toast.error(err?.message || t('tickets:validation.release.unreleaseError'));
      setUnreleasing(false);
      setConfirming(false);
    }
  }

  function field(label: string, value: string, set: (v: string) => void, invalid = false) {
    return (
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
        <Input
          value={value}
          onChange={(e) => set(e.target.value)}
          className={cn(invalid && 'border-red-300 bg-red-50 focus-visible:ring-red-200')}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 mb-4">
        <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
        <span className="text-xs font-semibold text-emerald-700">
          {t(appReleased ? 'tickets:validation.release.alreadyReleased' : 'tickets:validation.release.already')}
        </span>
      </div>

      {!hideMappingForm && (<>
      <h3 className="text-sm font-bold text-slate-800 mb-1">{t('tickets:validation.release.mappingTitle')}</h3>
      <p className="text-[11px] text-slate-400 mb-3">{t('tickets:validation.release.mappingSubtitle')}</p>

      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="col-span-2">{field(t('tickets:validation.release.mapStreet'), street, setStreet)}</div>
            {field(t('tickets:validation.release.mapHouseNumber'), houseNumber, setHouseNumber, houseExtIncoherent)}
            {field(t('tickets:validation.release.mapHouseExt'), houseExt, setHouseExt, houseExtIncoherent)}
            {field(t('tickets:validation.release.mapBus'), bus, setBus)}
            {field(t('tickets:validation.release.mapPostalCode'), postalCode, setPostalCode)}
            {field(t('tickets:validation.release.mapMunicipality'), municipality, setMunicipality)}
            {field(t('tickets:validation.release.mapCity'), city, setCity)}
            {/* Same postal-code check as the validation window's Panel 2 (user ask 2026-08-28): active
                only once the code differs from the loaded baseline, so simply opening the sheet is quiet.
                col-span-2 like the street field above — a list of localities squeezed into one column of
                a two-column grid wraps every name onto two lines (user report 2026-08-28, CP 1320). */}
            <div className="col-span-2">
            <PostalCodeLocalityHint
              postalCode={postalCode}
              city={city}
              active={baseline != null && postalCode !== baseline.postalCode}
              disabled={actionsDisabled}
              onPick={(c, m) => { setCity(c); setMunicipality(m); }}
            />
            </div>
          </div>

          {houseExtIncoherent && (
            <p className="text-xs font-medium text-red-600 mb-2" role="alert">{t('tickets:validation.houseExtIncoherent')}</p>
          )}
          {twins.length > 0 && (
            <p className="text-xs font-medium text-rose-600 mb-2" role="alert">
              {t('tickets:validation.declaredDup.formWarning', { count: twins.length, jvids: twins.join(', ') })}
            </p>
          )}

          {isDirty ? (
            // A field changed → the real Save-to-mapping action.
            <Button onClick={handleSaveMapping} disabled={saving || houseExtIncoherent || actionsDisabled} className="w-full mb-4">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
              {saving ? t('tickets:validation.release.mappingSaving') : t('tickets:validation.release.mappingSave')}
            </Button>
          ) : (
            // Nothing edited → no write is possible; offer a plain confirm that just closes the volet.
            <Button variant="outline" onClick={onClose} className="w-full mb-4">
              <Check className="w-4 h-4 mr-1.5" />{t('tickets:validation.release.mappingOk')}
            </Button>
          )}
        </>
      )}
      </>)}

      <div className="border-t border-slate-100 pt-3">
        {isConnected ? (
          // HOMES_CONNECT = an active customer connection → un-release is forbidden (would hide a live line).
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
            <p className="flex items-start gap-1.5 text-xs text-amber-800" role="note">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{t('tickets:validation.release.unreleaseBlockedConnect')}
            </p>
          </div>
        ) : !confirming ? (
          <Button
            variant="outline"
            onClick={() => setConfirming(true)}
            className="w-full text-amber-700 border-amber-300 hover:bg-amber-50"
          >
            <RotateCcw className="w-4 h-4 mr-1.5" />{t('tickets:validation.release.unreleaseButton')}
          </Button>
        ) : (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3">
            <p className="flex items-start gap-1.5 text-xs text-amber-800 mb-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{t('tickets:validation.release.unreleaseConfirm')}
            </p>
            <div className="flex gap-2">
              <Button onClick={handleUnrelease} disabled={unreleasing || actionsDisabled} className="flex-1 bg-amber-600 hover:bg-amber-700">
                {unreleasing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
                {unreleasing ? t('tickets:validation.release.unreleasing') : t('tickets:validation.release.unreleaseConfirmYes')}
              </Button>
              <Button variant="outline" onClick={() => setConfirming(false)} disabled={unreleasing} className="flex-1">
                {t('tickets:validation.release.cancel')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
